import React, { useEffect, useMemo, useState } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import DocumentCard from '../components/shared/DocumentCard';
import confirmEmailImage from '../assets/images/confirm-email.png';
import CreateDocumentModal from '../components/modals/createDocumentModal';
import Step1ImportDocModal from '../components/modals/import-document/step1-import-doc-modal';
import CreateDocIcon from '../assets/icons/createDocIcon';
import ImportDocIcon from '../assets/icons/importDocIcon';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../components/AuthProvider';
import { DocumentService } from '../services/DocumentService';
import { downloadDocumentPdfFromDashboard } from '../services/DocumentExportService';
import { Toast } from '../components/shared/Toast';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { QuickTourTooltip } from '../components/shared/QuickTourTooltip';
import { useSubscription } from '../hooks/useSubscription';
import { getPlanFeatures } from '../services/SubscriptionService';
import { filterDocumentsVisibleForPlan } from '../utils/documentTypePlanUtils';

export default function UserDashboardPage() {
	const [query, setQuery] = useState('');
	const [documents, setDocuments] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [refreshKey, setRefreshKey] = useState(0);
	const [isCreateDocumentModalOpen, setIsCreateDocumentModalOpen] = useState(false);
	const [isStep2CreateDocumentModalOpen, setIsStep2CreateDocumentModalOpen] = useState(false);
	const [isImportDocModalOpen, setIsImportDocModalOpen] = useState(false);
	const [importedFile, setImportedFile] = useState(null);
	const [tourRun, setTourRun] = useState(false);
	const [downloadingId, setDownloadingId] = useState(null);
	const navigate = useNavigate();
	const { user } = useAuthContext();
	const { getEffectivePlanName } = useSubscription();
	const effectivePlan = getEffectivePlanName();
	const canImportDocument = getPlanFeatures(effectivePlan)?.canImportDocument ?? false;

	const tourSteps = useMemo(
		() => [
			{
				target: '#quick-actions',
				placement: 'bottom',
				spotlightPadding: 12,
				content: (
					<div className="text-left">
						<h3 className="text-dark-blue font-semibold mb-1">Quick actions</h3>
						<p className="text-sm text-customGray">
							Create a new document or import an existing Word draft from here.
						</p>
					</div>
				),
				disableBeacon: true,
				hideCloseButton: true
			},
			{
				target: '#recent-documents',
				placement: 'bottom',
				spotlightPadding: 12,
				content: (
					<div className="text-left">
						<h3 className="text-dark-blue font-semibold mb-1">Recent documents</h3>
						<p className="text-sm text-customGray">
							Open, download, or continue working on documents you&apos;ve edited recently.
						</p>
					</div>
				),
				disableBeacon: true,
				hideCloseButton: true
			},
			{
				target: '#dashboard-search',
				placement: 'bottom',
				spotlightPadding: 8,
				content: (
					<div className="text-left">
						<h3 className="text-dark-blue font-semibold mb-1">Search</h3>
						<p className="text-sm text-customGray">
							Find a document quickly by typing part of its title.
						</p>
					</div>
				),
				disableBeacon: true,
				hideCloseButton: true
			},
			{
				target: '#sidebar-new-document',
				placement: 'right',
				spotlightPadding: 8,
				content: (
					<div className="text-left">
						<h3 className="text-dark-blue font-semibold mb-1">New document</h3>
						<p className="text-sm text-customGray">
							Start a new paper, thesis, or dissertation from anywhere in the app.
						</p>
					</div>
				),
				disableBeacon: true,
				hideCloseButton: true
			},
			{
				target: '#sidebar-nav-documents',
				placement: 'right',
				spotlightPadding: 8,
				content: (
					<div className="text-left">
						<h3 className="text-dark-blue font-semibold mb-1">My Documents</h3>
						<p className="text-sm text-customGray">
							Browse your full library, filter by status or type, and manage every document.
						</p>
					</div>
				),
				disableBeacon: true,
				hideCloseButton: true
			},
			{
				target: '#sidebar-nav-subscription',
				placement: 'right',
				spotlightPadding: 8,
				content: (
					<div className="text-left">
						<h3 className="text-dark-blue font-semibold mb-1">Subscription</h3>
						<p className="text-sm text-customGray">
							View your plan, upgrade tiers, and manage billing.
						</p>
					</div>
				),
				disableBeacon: true,
				hideCloseButton: true
			},
			{
				target: '#sidebar-nav-help',
				placement: 'right',
				spotlightPadding: 8,
				content: (
					<div className="text-left">
						<h3 className="text-dark-blue font-semibold mb-1">Support</h3>
						<p className="text-sm text-customGray">
							Report an issue or request help from the WriteForm team.
						</p>
					</div>
				),
				disableBeacon: true,
				hideCloseButton: true
			}
		],
		[]
	);

	const handleJoyrideCallback = (data) => {
		const { status } = data || {};
		const element = document.getElementById('main_dashboard');
		const mainEl = element ? element.querySelector('main') : null;
		if (data?.index === 1) {
			element.classList.remove('lg:h-screen');
		}
		if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
			setTourRun(false);
			element.classList.add('lg:h-screen');
			// Joyride's inline overflow or any inline styles are removed
			const cleanup = () => {
				if (mainEl) {
					if (mainEl.style && mainEl.style.removeProperty) {
						mainEl.style.removeProperty('overflow');
					}
					if (mainEl.hasAttribute('style')) {
						mainEl.removeAttribute('style');
					}
				}
			};
			cleanup();
			// setTimeout(cleanup, 0);
		}
	};

	const handleDownloadFromCard = async (id) => {
		if (!id) return;
		// Prevent multiple simultaneous downloads for same/dashboard
		if (downloadingId) return;
		setDownloadingId(id);
		try {
			await downloadDocumentPdfFromDashboard(id);
		} finally {
			setDownloadingId(null);
		}
	};

	const visibleDocuments = useMemo(
		() => filterDocumentsVisibleForPlan(effectivePlan, documents),
		[effectivePlan, documents]
	);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		return visibleDocuments.filter((doc) => {
			const title = doc?.title || '';
			const matchesQuery = !q ? true : title.toLowerCase().includes(q);
			return matchesQuery;
		});
	}, [query, visibleDocuments]);

	useEffect(() => {
		let mounted = true;
		async function loadRecent() {
			try {
				setLoading(true);
				setError('');
				const data = await DocumentService.fetchDocuments({ limit: 8 });
				const apiDocs = Array.isArray(data?.documents) ? data.documents : [];
				if (mounted) {
					setDocuments(apiDocs);
				}
			} catch (e) {
				const message =
					e?.response?.data?.message || e?.message || 'Failed to load recent documents';
				if (mounted) setError(message);
				Toast.error(message);
			} finally {
				if (mounted) setLoading(false);
			}
		}
		loadRecent();
		return () => {
			mounted = false;
		};
	}, [refreshKey]);

	// Fix scroll activation when transitioning from 0 documents to having documents
	useEffect(() => {
		if (!loading && visibleDocuments.length > 0) {
			// Use requestAnimationFrame to ensure DOM has fully rendered
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					const mainEl = document.querySelector('main');
					const container = document.getElementById('main_dashboard');

					if (mainEl && container) {
						// Force complete reflow by manipulating both container and main element
						const originalMainDisplay = mainEl.style.display;

						// Force reflow
						mainEl.style.display = 'none';
						void container.offsetHeight; // Trigger reflow
						mainEl.style.display = originalMainDisplay || '';

						// Ensure container maintains proper height class
						if (!container.classList.contains('lg:h-screen')) {
							container.classList.add('lg:h-screen');
						}

						// Force scroll recalculation by accessing scrollTop
						void mainEl.scrollTop;
					}
				});
			});
		}
	}, [loading, visibleDocuments.length]);

	useEffect(() => {
		const onUpdated = () => setRefreshKey((k) => k + 1);
		window.addEventListener('documents:updated', onUpdated);
		return () => window.removeEventListener('documents:updated', onUpdated);
	}, []);
	// listen to event when it is triggered from teh navbar quick tour button click
	useEffect(() => {
		const startTour = () => {
			try {
				const firstTarget = tourSteps?.[0]?.target;
				if (firstTarget) {
					const node = document.querySelector(firstTarget);
					if (node && typeof node.scrollIntoView === 'function') {
						node.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
					}
				}
			} catch {}
			// Give the browser a tick to settle the scroll before starting the tour
			setTimeout(() => setTourRun(true), 150);
		};
		window.addEventListener('dashboard-tour:start', startTour);
		return () => window.removeEventListener('dashboard-tour:start', startTour);
	}, [tourSteps]);

	const handleCreateNew = () => {
		setIsCreateDocumentModalOpen(true); // Open modal
	};
	const handleImport = () => {
		if (!canImportDocument) {
			Toast.error('Upgrade to Laureate to import Word documents.');
			return;
		}
		setImportedFile(null);
		setIsImportDocModalOpen(true);
	};

	const handleDocumentCreation = (documentId) => {
		if (!documentId) return;
		const searchParams = new URLSearchParams();
		searchParams.set('isCreated', 'true');
		if (importedFile) {
			searchParams.set('isImported', 'true');
		}
		setImportedFile(null);
		navigate(`/document-editor/${documentId}?${searchParams.toString()}`);
	};

	return (
		<div className="bg-bgColor">
			<Joyride
				callback={handleJoyrideCallback}
				continuous
				run={tourRun}
				scrollOffset={80}
				scrollToFirstStep
				showSkipButton
				steps={tourSteps}
				tooltipComponent={QuickTourTooltip}
				styles={{ options: { zIndex: 10000, arrowColor: 'transparent' } }}
				locale={{ last: 'End', skip: 'Skip', next: 'Next', back: 'Previous' }}
			/>
			{/* Header */}
			<div className="mb-14 flex flex-col items-center justify-between md:flex-row">
				<div>
					<h1 className="text-main font-bold leading-100 text-dark-blue">
						Welcome, {user.firstName}
					</h1>
					<p className="mt-1 text-sm text-customGray">
						Create, upload and manage your documents.
					</p>
				</div>

				<div className="flex items-center gap-3 flex-col md:flex-row">
					{/* Search */}
					<div className="relative w-80 my-5 md:my-0">
						<input
							id="dashboard-search"
							type="text"
							placeholder="Search document..."
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							className="h-[52px] w-full rounded-lg border border-lightGrey pl-4 pr-10 focus:outline-none focus:border-purple"
						/>
						<svg
							className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<circle cx="11" cy="11" r="8"></circle>
							<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
						</svg>
					</div>
					{/* <button type="button" onClick={() => setTourRun(true)} className="h-[40px] px-3 rounded-lg border border-lightGrey bg-white text-dark-blue text-sm">
						Start Tour
					</button> */}
				</div>
			</div>

			{/* Quick actions: Create & Import (centered, column) */}
			<div id="quick-actions" className="mb-12 grid grid-cols-1 gap-4 md:grid-cols-2">
				{/* Create new document */}
				<button
					type="button"
					onClick={handleCreateNew}
					className="group flex w-full flex-col items-center justify-center gap-3 rounded-xl border border-[#EDEDED] border-dashed bg-white p-10 text-center transition hover:shadow-sm"
				>
					<div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#B67FE2]">
						{/* plus icon */}
						<CreateDocIcon width={24} height={28} className="inline-block text-white " />
					</div>
					<div>
						<div className="text-dark-blue font-semibold text-base">Create a new document.</div>
						<div className="text-customGray text-sm">
							Begin your document — we&apos;ll build the structure for you.
						</div>
					</div>
				</button>

				{/* Import documents - Laureate only */}
				<button
					type="button"
					onClick={handleImport}
					disabled={!canImportDocument}
					title={!canImportDocument ? 'Upgrade to Laureate to import Word documents.' : undefined}
					className={`group flex w-full flex-col items-center justify-center gap-3 rounded-xl border border-[#EDEDED] border-dashed p-10 text-center transition hover:shadow-sm ${canImportDocument ? 'bg-white' : 'bg-gray-50 cursor-not-allowed opacity-70'}`}
				>
					<div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F3EEFF]">
						{/* import icon */}
						<ImportDocIcon width={24} height={28} className="inline-block text-dark-blue " />
					</div>
					<div>
						<div className="text-dark-blue font-semibold text-base">Import document</div>
						<div className="text-customGray text-sm">
							{canImportDocument
								? "Upload your draft — we'll organize and format it for you."
								: 'Upgrade to Laureate to import Word documents.'}
						</div>
					</div>
				</button>
			</div>

			<div id="recent-documents">
				{/* Recent documents header */}
				<div className="mb-8 flex items-center justify-between">
					{loading ? (
						<>
							<Skeleton
								width={250}
								height={20}
								baseColor="#E5E7EB"
								highlightColor="#F3F4F6"
								duration={1.2}
								className="rounded"
							/>
						</>
					) : (
						<>
							<h2 className="text-dark-blue text-[22px] font-medium leading-100">
								Recent {filtered.length === 1 ? 'Document' : 'Documents'}
							</h2>
						</>
					)}
					<a
						href="my-documents"
						className="inline-flex items-center gap-2 text-gray text-20 underline leading-24"
					>
						View all documents
					</a>
				</div>

				{/* Cards */}
				<div
					className="mb-[50px] grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
					ref={(el) => {
						// Force scroll recalculation when grid renders with documents
						if (el && !loading && visibleDocuments.length > 0) {
							setTimeout(() => {
								const mainEl = document.querySelector('main');
								if (mainEl) {
									mainEl.style.overflowY = 'auto';
								}
							}, 100);
						}
					}}
				>
					{loading &&
						Array.from({ length: 8 }).map((_, idx) => (
							<div
								key={idx}
								className="flex flex-col items-start gap-4 sm:gap-5 w-full p-3 md:p-4 lg:p-[15px] pb-4 md:pb-5 rounded-[14px] sm:rounded-[18px] border border-[#F0F0F0] bg-white"
							>
								<div className="w-full h-32 sm:h-[150px] rounded-[14px] sm:rounded-[18px] overflow-hidden">
									<Skeleton
										height="100%"
										width="100%"
										baseColor="#E5E7EB"
										highlightColor="#F3F4F6"
										duration={1.2}
									/>
								</div>
								<div className="w-full flex justify-between items-start gap-2">
									<div className="w-3/4">
										<Skeleton height={20} width="80%" />
									</div>
									<div className="w-6 h-6">
										<Skeleton circle height={24} width={24} />
									</div>
								</div>
								<div className="w-full">
									<Skeleton count={3} height={12} className="mb-1" />
								</div>
								<div className="w-full flex items-center justify-between">
									<div className="flex gap-2">
										<Skeleton height={28} width={90} />
										<Skeleton height={28} width={110} />
									</div>
									<Skeleton circle height={32} width={32} />
								</div>
							</div>
						))}

					{!loading && error && (
						<div className="col-span-full flex items-center justify-center rounded-xl border border-dashed border-red-200 p-10 text-red-600">
							{error}
						</div>
					)}

					{!loading &&
						!error &&
						filtered.map((doc) => {
							const id = doc?._id;
							const imageSrc = doc?.format?.formatpictureurl || confirmEmailImage;
							const formatName = doc?.format?.name || '';
							const title = doc?.title || 'Untitled Document';
							const description = doc?.description;
							const status = doc?.status || '';
							return (
								<DocumentCard
									key={id}
									documentId={id}
									imageSrc={imageSrc}
									format={formatName}
									documentType={doc?.documentType?.name || ''}
									title={title}
									description={description}
									status={status}
									updatedAt={doc?.updatedAt}
									onClick={() => navigate(`/document-editor/${id}?isCreated=true`)}
									onOpenEditor={() => navigate(`/document-editor/${id}?isCreated=true`)}
									onDownload={() => handleDownloadFromCard(id)}
									isDownloading={downloadingId === id}
								/>
							);
						})}

					{!loading && !error && filtered.length === 0 && (
						<div className="col-span-full flex items-center justify-center rounded-xl border border-dashed border-gray-200 p-10 text-gray-500">
							{visibleDocuments.length === 0 ? (
								<>
									No documents yet. Start fresh or upload a draft, WriteForm will handle the
									structure for you.
								</>
							) : (
								<>No documents match your search. Try a different search term.</>
							)}
						</div>
					)}
				</div>
			</div>

			{/* Step 1: Import Document Modal */}
			<Step1ImportDocModal
				isOpen={isImportDocModalOpen}
				onClose={() => setIsImportDocModalOpen(false)}
				onFileSelect={(file) => setImportedFile(file)}
				onNextStep={(file) => {
					setIsImportDocModalOpen(false); // Close Step1 modal
					setImportedFile(file);
					setIsStep2CreateDocumentModalOpen(true); // Open Step2 modal
				}}
			/>

			{/* Step 2: Create Document Modal (import flow) */}
			<CreateDocumentModal
				isOpen={isStep2CreateDocumentModalOpen}
				onClose={() => { setIsStep2CreateDocumentModalOpen(false); setImportedFile(null); }}
				onDocumentCreated={handleDocumentCreation}
				isImported={true}
				importedFile={importedFile}
			/>

			{/* Step 2: Create Document Modal for "Create New" */}
			<CreateDocumentModal
				isOpen={isCreateDocumentModalOpen}
				onClose={() => setIsCreateDocumentModalOpen(false)}
				onDocumentCreated={handleDocumentCreation} // Pass the handler
				isImported={false}
			/>
		</div>
	);
}
